import { useCallback, useEffect, useRef, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { useLazyQuery } from '@apollo/client/react';
import {
  Box,
  CircularProgress,
  IconButton,
  MenuItem,
  OutlinedInput,
  Tab,
  Tabs,
} from '@mui/material';
import { sanitizeHtml } from '@realadvisor/html-sanitizer';
import { PlusIcon, Trash2Icon, XIcon } from 'lucide-react';

import { MarkdownComposer } from '@modules/_shared/components/MarkdownComposer';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { PopperMenu } from '@modules/_shared/components/PopperMenu';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { useReportContext } from '@modules/_shared/components/report-editor/ReportEditorContext';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { TRANSLATE_TEXT } from '@modules/reports/shared/translateQuery';
import { useLocale } from '@shared/hooks/locale';
import { type Language, languagesList } from '@shared/LocaleContext';
import { cssTokens } from '@shared/theme/StyleProvider';

export const LANGUAGE_API_NAMES: Record<Language, string> = {
  en: 'English',
  fr: 'French',
  de: 'German',
  it: 'Italian',
  es: 'Spanish',
  pl: 'Polish',
  pt: 'Portuguese',
};

type DescriptionLanguageTabsProps = {
  defaultLanguage?: Language | null;
  translations: Record<Language, string | null>;
  onTranslationChange: (language: Language, text: string) => void;
  onTranslationComplete?: (language: Language, text: string) => void;
  onTranslationDelete?: (language: Language) => void;
  onDescriptionLanguageChange?: (language: Language) => void;
  actions?: (
    activeTab: Language,
    setContent: (text: string) => void,
    translateFrom: (sourceLanguage: Language) => Promise<void>,
    sourceLanguages: Language[],
    isTranslating: boolean,
  ) => React.ReactNode;
  disabled?: boolean;
  variant?: 'rich-text' | 'plain-text';
};

export const DescriptionLanguageTabs = ({
  defaultLanguage,
  translations,
  onTranslationChange,
  onTranslationComplete,
  onTranslationDelete,
  onDescriptionLanguageChange,
  actions,
  disabled = false,
  variant = 'rich-text',
}: DescriptionLanguageTabsProps) => {
  const { t } = useLocale();
  const { language } = useReportContext();
  const menuAnchorRef = useRef<HTMLButtonElement>(null);

  const [activeTab, setActiveTab] = useState<Language>(
    defaultLanguage ?? language,
  );
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const [deleteDialogLanguage, setDeleteDialogLanguage] =
    useState<Language | null>(null);
  const [localTranslations, setLocalTranslations] =
    useState<Record<Language, string | null>>(translations);

  const handleTranslateError = useCallback(
    (error: ErrorLike) => {
      const isUntranslatable =
        CombinedGraphQLErrors.is(error) &&
        error.errors.some(
          e => e.extensions?.['code'] === 'UNTRANSLATABLE_TEXT',
        );
      if (isUntranslatable) {
        setSnackbarMessage(
          t(
            'The text could not be translated. Please provide meaningful content.',
          ),
        );
      } else {
        setErrorLike(error);
      }
    },
    [t],
  );

  const [translateText, { loading: isTranslating }] = useLazyQuery(
    TRANSLATE_TEXT,
    {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
    },
  );

  useEffect(() => {
    const shouldBumpEditorKey =
      disabled ||
      (localTranslations[activeTab] == null && translations[activeTab] != null);

    setLocalTranslations(prev => {
      const next = { ...prev };
      for (const lang of languagesList) {
        if (lang !== activeTab || disabled || prev[lang] == null) {
          next[lang] = translations[lang];
        }
      }
      return next;
    });

    if (shouldBumpEditorKey) {
      setEditorKey(prev => prev + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translations, activeTab, disabled]);

  const prevReportLanguage = useRef(language);
  useEffect(() => {
    if (prevReportLanguage.current !== language) {
      prevReportLanguage.current = language;
      setActiveTab(language);
      onDescriptionLanguageChange?.(language);
    }
  }, [language, onDescriptionLanguageChange]);

  const orderedLanguages = [
    language,
    ...languagesList.filter(code => code !== language),
  ];

  const visibleTabs = orderedLanguages.filter(
    code => code === language || localTranslations[code] != null,
  );

  const isActiveTabVisible =
    activeTab === language || localTranslations[activeTab] != null;
  useEffect(() => {
    if (!isActiveTabVisible) {
      setActiveTab(language);
      onDescriptionLanguageChange?.(language);
    }
  }, [isActiveTabVisible, language, onDescriptionLanguageChange]);

  const translatableLanguages = languagesList.filter(
    code => code !== activeTab && !localTranslations[code],
  );

  const runTranslation = useCallback(
    async (
      sourceLanguage: Language,
      targetLanguage: Language,
      onSuccess: (translatedText: string) => void,
    ) => {
      const { data, error } = await translateText({
        variables: {
          text: localTranslations[sourceLanguage] ?? '',
          target_language: targetLanguage,
        },
      });
      if (error) {
        handleTranslateError(error);
        return;
      }
      if (data?.translate_text?.text != null) {
        const translatedText = sanitizeHtml(data.translate_text.text);
        setLocalTranslations(prev => ({
          ...prev,
          [targetLanguage]: translatedText,
        }));
        (onTranslationComplete ?? onTranslationChange)(
          targetLanguage,
          translatedText,
        );
        onSuccess(translatedText);
      }
    },
    [
      localTranslations,
      translateText,
      onTranslationComplete,
      onTranslationChange,
      handleTranslateError,
    ],
  );

  const handleTranslate = useCallback(
    async (targetLanguage: Language) => {
      if (!localTranslations[activeTab]) {
        setSnackbarMessage(
          t(
            'Please write a description in the current language before translating.',
          ),
        );
        return;
      }
      await runTranslation(activeTab, targetLanguage, () => {
        setActiveTab(targetLanguage);
        onDescriptionLanguageChange?.(targetLanguage);
      });
    },
    [
      activeTab,
      localTranslations,
      runTranslation,
      onDescriptionLanguageChange,
      t,
    ],
  );

  const handleTranslateFrom = useCallback(
    async (sourceLanguage: Language) => {
      if (!localTranslations[sourceLanguage]) {
        setSnackbarMessage(
          t('The source language has no content to translate from.'),
        );
        return;
      }
      await runTranslation(sourceLanguage, activeTab, () => {
        setEditorKey(prev => prev + 1);
      });
    },
    [activeTab, localTranslations, runTranslation, t],
  );

  const sourceLanguages = languagesList.filter(
    code => code !== activeTab && !!localTranslations[code],
  );

  const handleValueChange = useCallback(
    (value: string) => {
      setLocalTranslations(prev => ({ ...prev, [activeTab]: value }));
      onTranslationChange(activeTab, value);
    },
    [activeTab, onTranslationChange],
  );

  const setContent = useCallback(
    (text: string) => {
      setLocalTranslations(prev => ({ ...prev, [activeTab]: text }));
      setEditorKey(prev => prev + 1);
    },
    [activeTab],
  );

  const handleMenuClose = (event: Event) => {
    if (menuAnchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }
    setMenuOpen(false);
  };

  return (
    <Box
      sx={{
        ...(disabled && {
          opacity: 0.5,
          pointerEvents: 'none',
        }),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, newValue: Language) => {
            if (isTranslating) {
              return;
            }

            const pendingValue = localTranslations[activeTab];
            if (
              pendingValue != null &&
              pendingValue !== translations[activeTab]
            ) {
              (onTranslationComplete ?? onTranslationChange)(
                activeTab,
                pendingValue,
              );
            }

            setActiveTab(newValue);
            onDescriptionLanguageChange?.(newValue);
          }}
          sx={{
            flex: 1,
            minHeight: 36,
            '& .MuiTab-root': {
              minHeight: 36,
              py: 0.5,
              px: 1.5,
            },
            ...(isTranslating && {
              opacity: 0.5,
              pointerEvents: 'none',
            }),
          }}
        >
          {visibleTabs.map(code => (
            <Tab
              key={code}
              value={code}
              label={
                code === language ? (
                  code.toUpperCase()
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {code.toUpperCase()}
                    <Box
                      component="span"
                      onClick={e => {
                        e.stopPropagation();
                        setDeleteDialogLanguage(code);
                      }}
                      sx={{
                        display: 'inline-flex',
                        opacity: 0.6,
                        '&:hover': { opacity: 1 },
                      }}
                    >
                      <XIcon size={14} />
                    </Box>
                  </Box>
                )
              }
            />
          ))}
        </Tabs>
        {translatableLanguages.length > 0 && (
          <>
            <IconButton
              ref={menuAnchorRef}
              size="small"
              disabled={isTranslating}
              onClick={() => setMenuOpen(prev => !prev)}
            >
              {isTranslating ? (
                <CircularProgress size={16} />
              ) : (
                <PlusIcon size={18} />
              )}
            </IconButton>
            <PopperMenu
              open={menuOpen}
              anchorEl={menuAnchorRef.current}
              onClose={handleMenuClose}
            >
              {translatableLanguages.map(code => (
                <MenuItem
                  key={code}
                  onClick={() => {
                    setMenuOpen(false);
                    handleTranslate(code);
                  }}
                >
                  {LANGUAGE_API_NAMES[code]}
                </MenuItem>
              ))}
            </PopperMenu>
          </>
        )}
      </Box>

      <Box sx={{ pt: 1, position: 'relative' }}>
        {variant === 'plain-text' ? (
          <OutlinedInput
            key={`${activeTab}-${editorKey}`}
            defaultValue={localTranslations[activeTab] ?? ''}
            onChange={e => handleValueChange(e.target.value)}
            placeholder={LANGUAGE_API_NAMES[activeTab]}
            fullWidth
            size="small"
            multiline
            minRows={1}
            maxRows={4}
            disabled={disabled}
            sx={{ background: 'var(--mui-palette-background-paper)' }}
          />
        ) : (
          <MarkdownComposer
            key={`${activeTab}-${editorKey}`}
            initialValue={localTranslations[activeTab] ?? ''}
            onChange={handleValueChange}
            placeholder={LANGUAGE_API_NAMES[activeTab]}
            format="html"
            preserveLineBreaks
            readOnly={disabled}
            actions={actions?.(
              activeTab,
              setContent,
              handleTranslateFrom,
              sourceLanguages,
              isTranslating,
            )}
          />
        )}
        {isTranslating && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              mt: 1,
              bgcolor: cssTokens.backdrop,
              borderRadius: 1,
              zIndex: 1,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'sticky',
                top: '10%',
                display: 'flex',
                justifyContent: 'center',
                py: 2,
              }}
            >
              <CircularProgress size={24} disableShrink />
            </Box>
          </Box>
        )}
      </Box>

      <SlideDialog
        open={deleteDialogLanguage != null}
        onClose={() => setDeleteDialogLanguage(null)}
        title={t('Delete translation')}
        onSave={() => {
          setLocalTranslations(prev => ({
            ...prev,
            [deleteDialogLanguage!]: null,
          }));
          onTranslationDelete?.(deleteDialogLanguage!);
          if (activeTab === deleteDialogLanguage) {
            setActiveTab(language);
            onDescriptionLanguageChange?.(language);
          }
          setDeleteDialogLanguage(null);
        }}
        saveText={t('Delete')}
        saveColor="error"
        saveIcon={<Trash2Icon />}
      >
        <Box sx={{ p: 2 }}>
          {t(
            'Are you sure you want to delete this translation? This action cannot be undone.',
          )}
        </Box>
      </SlideDialog>

      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />

      {snackbarMessage && (
        <RASnackbar
          message={snackbarMessage}
          onClose={() => setSnackbarMessage(null)}
          severity="error"
        />
      )}
    </Box>
  );
};
